import { useDispatch, useSelector } from 'react-redux'
import {
  selectAddressCurrentContractActive,
  selectCharacteristicsCurrentContractActive,
  selectConsumptionCurrentContractActive,
  selectElecCurrentContractActive,
  selectProviderConfigCurrentContractActive,
} from '../../SubscriptionForm/SubscriptionFormSelectors'
import useRetrieveActiveContractIdAndType from '../../Layouts/useRetrieveActiveContractIdAndType'
import { updateContractConsumptionData, updateContractElecData } from '../../SubscriptionForm/SubscriptionFormSlice'
import {
  retrieveConsumptionDataFromEnedis,
  retrieveMeterDataFromEnedis,
} from '../ElectricityMeter/pdlEigibilityCallback'
import { selectAuthData } from '@/containers/Login/AuthSelectors'
import { modulesConfig } from '@/containers/SubscriptionFormPageNew/config'
import { PRESTATION_TYPE_CDF } from '../../SubscriptionForm/Common/constants'

const useEnedisPrefillData = () => {
  const dispatch = useDispatch()
  const { id } = useRetrieveActiveContractIdAndType()
  const { country } = useSelector(selectAuthData)
  const { electricityMeter, gasMeter, energyLandscape = {} } = useSelector(selectAddressCurrentContractActive)
  const { allowConsumptionHistoricOnMes, minimumCarElectricity } = useSelector(
    selectProviderConfigCurrentContractActive,
  )
  const { carBase, carHp, carHc } = useSelector(selectConsumptionCurrentContractActive)
  const { prestationType } = useSelector(selectCharacteristicsCurrentContractActive)
  const { meterPower, meterOption, originalMeterPower, originalMeterOption } = useSelector(
    selectElecCurrentContractActive,
  )
  const { consumptionEstimation = {} } = energyLandscape
  const {
    car_elec_base: carElecBase = null,
    car_elec_hp: carElecHP = null,
    car_elec_hc: carElecHC = null,
    car_gas: carGasConsumption = null,
  } = consumptionEstimation

  // Set back pre-filled data in case it was selected a first time before
  // we have to do the computation again
  const resetElectMeterData = () => {
    dispatch(
      updateContractElecData({
        data: {
          meterPower: null,
          meterOption: null,
        },
        id,
      }),
    )
  }
  const resetGasConsumptionData = () => {
    dispatch(
      updateContractConsumptionData({
        id,
        data: {
          carGas: carGasConsumption,
          carGasOriginal: carGasConsumption,
          monthlyAmount: null,
          amounts: { total: [], elec: [], gas: [] },
          prefilled: {
            carGas: { status: true, message: "Les données de consommation proviennent de l'outil d'estimation." },
          },
        },
      }),
    )
  }

  const resetElecConsumptionData = () => {
    dispatch(
      updateContractConsumptionData({
        id,
        data: {
          carBase: carElecBase,
          carBaseOriginal: carElecBase,
          carHp: carElecHP,
          carHpOriginal: carElecHP,
          carHc: carElecHC,
          carHcOriginal: carElecHC,
          monthlyAmount: null,
          amounts: { total: [], elec: [], gas: [] },
          prefilled: {
            carBase: { status: true, message: "Les données de consommation proviennent de l'outil d'estimation." },
            carHp: { status: true, message: "Les données de consommation proviennent de l'outil d'estimation." },
            carHc: { status: true, message: "Les données de consommation proviennent de l'outil d'estimation." },
          },
        },
      }),
    )
  }

  const handleElectricityPrefill = energyType => {
    if (energyType !== 'elec' && energyType !== 'dual') {
      resetElectMeterData()
      resetElecConsumptionData()
      return
    }
    if (electricityMeter?.pdlEligibilityData?.elec?.technicalData) {
      const dontAllowChangesOnMes =
        (prestationType === 'mes' || prestationType === 'fmes') && allowConsumptionHistoricOnMes === 0
      const eligibilityData = electricityMeter.pdlEligibilityData.elec.technicalData
      const annualCar = electricityMeter.pdlEligibilityData.elec?.consumption?.yearlyExtrapolation ?? 0
      const enedisMeterOption = eligibilityData.rawMeterOption ? eligibilityData.rawMeterOption.toLowerCase() : ''
      const meterData = retrieveMeterDataFromEnedis(eligibilityData, {
        meterOption,
        originalMeterOption,
        meterPower,
        originalMeterPower,
      })
      dispatch(
        updateContractElecData({
          data: { ...meterData },
          id,
        }),
      )
      if (
        parseInt(annualCar) > 0 &&
        electricityMeter.pdlEligibilityData.elec?.consumption?.days &&
        parseInt(electricityMeter.pdlEligibilityData.elec?.consumption?.days) >= 90 &&
        !dontAllowChangesOnMes
      ) {
        // FR + CDF only: pass the provider minimum so the CAR is clamped up to it when Enedis
        // returns a lower value with more than 365 days of history.
        const minimumCarForClamp =
          country === 'fr' && prestationType === PRESTATION_TYPE_CDF ? minimumCarElectricity : null
        const enedisConsumption = retrieveConsumptionDataFromEnedis(
          parseInt(annualCar),
          electricityMeter.pdlEligibilityData.elec?.consumption?.days ?? 0,
          { carBase, carHp, carHc },
          enedisMeterOption,
          minimumCarForClamp,
        )
        if (enedisConsumption === null) {
          return
        }
        if (enedisConsumption.error) {
          // todo-v2: handle error
          return
        }
        dispatch(
          updateContractConsumptionData({
            id,
            data: {
              ...enedisConsumption.consumption,
            },
          }),
        )
      } else {
        //use consumption estimation
        resetElecConsumptionData()
      }
    } else {
      resetElectMeterData()
      resetElecConsumptionData()
    }
  }

  const handleGasPrefill = energyType => {
    if (energyType !== 'gas' && energyType !== 'dual') {
      resetGasConsumptionData()
    }
    if (gasMeter?.pceEligibilityData?.gas) {
      const dontAllowChangesOnMes =
        (prestationType === 'mes' || prestationType === 'fmes') && allowConsumptionHistoricOnMes === 0
      const annualCar = gasMeter.pceEligibilityData.gas?.yearlyExtrapolation ?? 0

      if (parseInt(annualCar) > 0 && !dontAllowChangesOnMes) {
        dispatch(
          updateContractConsumptionData({
            id,
            data: {
              carGas: parseInt(annualCar),
              carGasOriginal: parseInt(annualCar),
              monthlyAmount: null,
              amounts: { total: [], elec: [], gas: [] },
              prefilled: {
                carGas: {
                  status: true,
                  message: 'Les données de consommation proviennent de GRDF.',
                },
              },
            },
          }),
        )
      } else {
        //use consumption estimation
        resetGasConsumptionData()
      }
    } else {
      resetGasConsumptionData()
    }
  }

  const prefillEnedisData = energyType => {
    if (!modulesConfig[country]?.contract?.prefillEnedisData) return
    handleElectricityPrefill(energyType)
    handleGasPrefill(energyType)
  }
  return {
    prefillEnedisData,
  }
}

export default useEnedisPrefillData
